import { Center, Loader } from "@mantine/core";
import { toRelativeUrl } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import { ReactNode, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  clearClaimReauthAttempts,
  getAccessTokenClaims,
  getClaimReauthAttempts,
  hasDeliveryPortalGroupsClaim,
  incrementClaimReauthAttempts,
  MAX_CLAIM_REAUTH_ATTEMPTS,
} from "../helpers/authHelper";

/**
 * Re-authenticates when the access token is missing DeliveryPortal_groups
 * (required by the API authorizer).
 */
export const AuthClaimValidator = ({ children }: { children: ReactNode }) => {
  const { oktaAuth, authState } = useOktaAuth();
  const navigate = useNavigate();
  const redirectStartedRef = useRef(false);

  const isAuthenticated = authState?.isAuthenticated ?? false;
  const accessToken = authState?.accessToken;
  const hasValidClaim = hasDeliveryPortalGroupsClaim(
    getAccessTokenClaims(accessToken)
  );
  const waitingForToken = isAuthenticated && !accessToken;
  const needsClaimReauth =
    isAuthenticated && !!accessToken && !hasValidClaim;
  const reauthAttempts = getClaimReauthAttempts();
  const exceededMaxReauthAttempts =
    needsClaimReauth && reauthAttempts >= MAX_CLAIM_REAUTH_ATTEMPTS;

  useEffect(() => {
    if (!needsClaimReauth) {
      if (hasValidClaim) {
        clearClaimReauthAttempts();
      }
      redirectStartedRef.current = false;
      return;
    }

    if (exceededMaxReauthAttempts) {
      navigate("/not-authorized", { replace: true });
      return;
    }

    if (redirectStartedRef.current) {
      return;
    }

    redirectStartedRef.current = true;
    incrementClaimReauthAttempts();
    oktaAuth.setOriginalUri(
      toRelativeUrl(window.location.href, window.location.origin)
    );
    void oktaAuth.signInWithRedirect().catch(() => {
      redirectStartedRef.current = false;
    });
  }, [
    exceededMaxReauthAttempts,
    hasValidClaim,
    navigate,
    needsClaimReauth,
    oktaAuth,
  ]);

  if (waitingForToken || needsClaimReauth) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  return <>{children}</>;
};
